import { defineStore } from 'pinia'
import { subscriptionService } from '@/services/subscriptionService'
import type {
  SubscriptionPayment,
  PaginationInfo,
  PaymentFilters,
  PaymentStatus
} from '@/types/subscription'
import { handleAuthError, extractErrorMessage } from '@/utils/storeHelpers'
import {
  isPaginatedPaymentsResponse,
  createPaginationFromArray,
  paginateArrayData,
  extractPaymentsArray,
  createPaginationInfo,
  calculateStatusCounts,
  hasNextPage
} from '@/utils/subscriptionHelpers'

export const usePaymentsStore = defineStore('payments', {
  state: () => ({
    payments: [] as SubscriptionPayment[],
    loading: false,
    error: null as string | null,
    pagination: null as PaginationInfo | null,
    totalCount: 0,
    allCount: 0, // Total count of all payments (unfiltered)
    statusCounts: {} as Record<PaymentStatus, number>,
    filters: {
      page: 1,
      page_size: 10,
      ordering: '-payment_date'
    } as PaymentFilters,
    isOnlyUpdate: false, // Flag for tracking payments-only updates
  }),

  getters: {
    hasPayments: (state) => state.payments.length > 0,
    
    currentPage: (state) => state.filters.page,
    
    pageSize: (state) => state.filters.page_size,
    
    hasNextPage: (state) => {
      return state.pagination?.has_next || false
    },
    
    hasPreviousPage: (state) => {
      return state.pagination?.has_previous || false
    },
    
    totalPages: (state) => {
      return state.pagination?.total_pages || 1
    },
  },

  actions: {
    async fetchPayments(filters?: Partial<PaymentFilters>) {
      this.loading = true
      this.error = null
      this.isOnlyUpdate = true

      try {
        const currentFilters = this.mergeAndCleanFilters(filters)
        this.filters = currentFilters

        const queryString = subscriptionService.buildPaymentsQueryString(currentFilters)
        const response = await subscriptionService.getPayments(queryString)
        const responseData = response.data

        if (isPaginatedPaymentsResponse(responseData)) {
          this.handlePaginatedPaymentsResponse(responseData, currentFilters)
        } else {
          this.handleArrayPaymentsResponse(responseData, currentFilters)
        }
      } catch (error: any) {
        this.handlePaymentsFetchError(error)
      } finally {
        this.loading = false
        this.isOnlyUpdate = false
      }
    },

    mergeAndCleanFilters(filters?: Partial<PaymentFilters>): PaymentFilters {
      const currentFilters = {
        ...this.filters,
        ...filters
      } as PaymentFilters

      // If status is not provided in new filters, remove it from current filters
      if (filters && !('status' in filters)) {
        delete currentFilters.status
      }

      return currentFilters
    },

    handlePaginatedPaymentsResponse(paginatedData: any, currentFilters: PaymentFilters) {
      this.payments = paginatedData.results
      this.totalCount = paginatedData.count || paginatedData.results.length

      // Use page_info from API or create from total count
      this.pagination = paginatedData.page_info
        ? paginatedData.page_info
        : createPaginationInfo(paginatedData.count, currentFilters)
    },

    handleArrayPaymentsResponse(responseData: any, currentFilters: PaymentFilters) {
      const allPayments = extractPaymentsArray(responseData)
      this.totalCount = allPayments.length
      this.payments = allPayments.length ? paginateArrayData(allPayments, currentFilters) : []
      this.pagination = createPaginationFromArray(allPayments, currentFilters)
    },

    handlePaymentsFetchError(error: any) {
      if (handleAuthError(error)) {
        this.error = 'Current user is not authorized to access payments'
        return
      }

      // If API endpoint doesn't exist (404), initialize with empty data
      if (error.response?.status === 404) {
        this.payments = []
        this.pagination = null
        this.totalCount = 0
        this.error = null
        return
      }

      this.error = extractErrorMessage(error, 'Failed to fetch payments')
    },

    // Payment pagination methods
    async goToPage(page: number) {
      await this.fetchPayments({ page })
    },

    async filterPayments(filters: Partial<PaymentFilters>) {
      await this.fetchPayments({ page: 1, ...filters })
    },

    // Initialize status counts from payments array
    initializeStatusCountsFromPayments(payments: SubscriptionPayment[]) {
      this.allCount = payments.length
      this.statusCounts = calculateStatusCounts(payments)
    },

    // Get status counts for tabs
    async fetchStatusCounts() {
      try {
        const allPayments = await this.fetchAllPayments()
        this.initializeStatusCountsFromPayments(allPayments)
      } catch (error: any) {
        // If fetchAllPayments fails, use currently loaded payments for status counts
        console.warn('Failed to fetch all payments for status counts, using current payments:', error)
        this.initializeStatusCountsFromPayments(this.payments)
        throw error
      }
    },

    async fetchAllPayments(): Promise<SubscriptionPayment[]> {
      const FETCH_PAGE_SIZE = 100
      let allPayments: SubscriptionPayment[] = []
      let currentPage = 1
      let hasMorePages = true

      while (hasMorePages) {
        const response = await subscriptionService.getPaymentsByPage(currentPage, FETCH_PAGE_SIZE)
        const responseData = response.data

        if (isPaginatedPaymentsResponse(responseData)) {
          allPayments = [...allPayments, ...responseData.results]
          hasMorePages = hasNextPage(responseData, currentPage, FETCH_PAGE_SIZE)
          currentPage++
        } else {
          // Non-paginated response, get all at once
          allPayments = extractPaymentsArray(responseData)
          hasMorePages = false
        }
      }

      return allPayments
    },

    async initializePaymentsData() {
      // Fetch payments first (critical)
      await this.fetchPayments()
      
      // Fetch status counts (non-critical, can fail without blocking)
      try {
        await this.fetchStatusCounts()
      } catch (statusCountsError) {
        // Status counts already initialized with fallback data in fetchStatusCounts
        // No need to re-throw as this is non-critical
      }
    },
  }
})

